import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi, getAdminToken } from "../../lib/adminApi";
import { useAuth } from "../../context/AuthContext";
import { useSiteContent } from "../../context/SiteContentContext";
import { ArrowDown, ArrowUp, ImagePlus } from "lucide-react";
import { SERVICES, EVENTS, FOCUS_AREAS, GALLERY_IMAGES } from "../../data/content";
import { formatEventDate, isUpcomingEvent, normalizeEvent } from "../../lib/events";

function Section({ title, children }) {
  return (
    <section className="rounded-3xl bg-white border border-black/5 shadow-ambient p-6 md:p-8">
      <h2 className="font-display text-2xl">{title}</h2>
      <div className="mt-6 space-y-5">{children}</div>
    </section>
  );
}

function Field({ label, ...props }) {
  const Tag = props.rows ? "textarea" : "input";
  return (
    <label className="block">
      <span className="font-mono text-xs uppercase tracking-[0.2em] text-black/55">{label}</span>
      <Tag className="cf-input mt-2" {...props} />
    </label>
  );
}

function persistableImage(src = "") {
  if (!src || String(src).includes("/static/media/")) return "";
  return src;
}

function toFocusForm(list) {
  return list.map((item, i) => ({
    id: item.id || `focus-${i + 1}`,
    title: item.title || "",
    text: item.text || "",
    bullets: item.bullets || [],
    image: item.image || "",
  }));
}

function emptyFocusArea() {
  return {
    id: `focus-${Date.now()}`,
    title: "",
    text: "",
    bullets: [],
    image: "",
  };
}

function toGalleryForm(list) {
  return (list || []).map((item, i) => ({
    id: item.id || `gal-${i + 1}`,
    src: item.src || "",
    caption: item.caption || "",
  }));
}

function moveItem(list, index, direction) {
  const next = [...list];
  const target = index + direction;
  if (target < 0 || target >= next.length) return next;
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

export default function AdminContentPage() {
  const { can } = useAuth();
  const { org, stats, focusAreas, events, gallery, services, programmes, hero, refresh } = useSiteContent();
  const [orgForm, setOrgForm] = useState(org);
  const [heroForm, setHeroForm] = useState(hero);
  const [statsForm, setStatsForm] = useState(stats);
  const [focusForm, setFocusForm] = useState(() => toFocusForm(focusAreas));
  const toServiceForm = (list) =>
    list.map(({ title, text, bullets, image, accent }) => ({
      title: title || "",
      text: text || "",
      bullets: bullets || [],
      image: image || "",
      accent: accent || "primary",
    }));

  const [servicesForm, setServicesForm] = useState(() => toServiceForm(services));
  const [programmesForm, setProgrammesForm] = useState(programmes);
  const [eventsForm, setEventsForm] = useState(() => events.map((item, i) => normalizeEvent(item, i)));
  const [galleryForm, setGalleryForm] = useState(() => toGalleryForm(gallery));
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState("");

  useEffect(() => {
    setOrgForm(org);
    setHeroForm(hero);
    setStatsForm(stats);
    setFocusForm(toFocusForm(focusAreas));
    setServicesForm(toServiceForm(services));
    setProgrammesForm(programmes);
    setEventsForm(events.map((item, i) => normalizeEvent(item, i)));
    setGalleryForm(toGalleryForm(gallery));
  }, [org, hero, stats, focusAreas, events, gallery, services, programmes]);

  const token = useMemo(() => getAdminToken(), []);
  const canOrg = can("website.org", "edit");
  const canHero = can("website.hero", "edit");
  const canStats = can("website.stats", "edit");
  const canFocus = can("website.focus", "edit");
  const canServices = can("website.services", "edit");
  const canProgrammes = can("website.programmes", "edit");
  const canEvents = can("website.events", "edit");
  const canGallery = can("website.gallery", "edit");
  const hasAnySection = canOrg || canHero || canStats || canFocus || canServices || canProgrammes || canEvents || canGallery;
  const showVideosLink = can("videos", "view");

  const save = async (key, value) => {
    setSaving(true);
    setMessage("");
    try {
      await adminApi.upsertContent(token, key, value);
      await refresh();
      setMessage("Saved.");
    } catch (err) {
      setMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  const uploadFocusImage = async (index, file) => {
    if (!file) return;
    setUploadingKey(focusForm[index]?.id || String(index));
    setMessage("");
    try {
      const src = await adminApi.uploadImage(file);
      setFocusForm((current) => current.map((item, i) => (i === index ? { ...item, image: src } : item)));
      setMessage("Image uploaded. Save focus areas to publish it on the site.");
    } catch (err) {
      setMessage(err.message);
    } finally {
      setUploadingKey("");
    }
  };

  const uploadGalleryFiles = async (files) => {
    const list = Array.from(files || []).filter((file) => file.type.startsWith("image/"));
    if (!list.length) return;
    setUploadingKey("gallery");
    setMessage("");
    try {
      const urls = await adminApi.uploadImages(list);
      setGalleryForm((current) => [
        ...current,
        ...urls.map((src, i) => ({
          id: `gal-${Date.now()}-${i}`,
          src,
          caption: "",
        })),
      ]);
      setMessage(
        `${urls.length} image${urls.length === 1 ? "" : "s"} uploaded. Save gallery to publish ${urls.length === 1 ? "it" : "them"} on the site.`
      );
    } catch (err) {
      setMessage(err.message);
    } finally {
      setUploadingKey("");
    }
  };

  const replaceGalleryImage = async (index, file) => {
    if (!file) return;
    setUploadingKey(galleryForm[index]?.id || String(index));
    setMessage("");
    try {
      const src = await adminApi.uploadImage(file);
      setGalleryForm((current) => current.map((item, i) => (i === index ? { ...item, src } : item)));
      setMessage("Photo replaced. Save gallery to publish it on the site.");
    } catch (err) {
      setMessage(err.message);
    } finally {
      setUploadingKey("");
    }
  };

  return (
    <div>
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#EC4899]">CMS</p>
      <h1 className="font-display text-4xl md:text-5xl mt-2">Website content</h1>
      <p className="text-black/60 mt-3">Changes go live on the public site after you save each section.</p>
      {showVideosLink && (
        <p className="text-sm text-black/55 mt-2">
          Project films are managed separately in <Link to="/admin/videos" className="text-[#EC4899] hover:underline">Videos</Link>.
        </p>
      )}
      {message && (
        <p className={`mt-4 text-sm ${/saved|uploaded|replaced/i.test(message) ? "text-black/70" : "text-[#BE185D]"}`}>
          {message}
        </p>
      )}

      <div className="space-y-6 mt-8">
        {!hasAnySection && (
          <p className="rounded-3xl bg-white border border-black/5 p-6 text-sm text-black/55">
            You can open Website, but no content sections have been granted for this account. Ask a superadmin to update your permissions.
          </p>
        )}
        {canOrg && (
        <Section title="Contact details">
          <Field label="Email" type="email" value={orgForm.email || ""} onChange={(e) => setOrgForm({ ...orgForm, email: e.target.value })} />
          {(orgForm.phones || []).map((phone, i) => (
            <Field
              key={i}
              label={`Phone ${i + 1}`}
              value={phone.display || ""}
              onChange={(e) => {
                const phones = [...orgForm.phones];
                const display = e.target.value;
                phones[i] = { display, tel: `+44${display.replace(/\D/g, "").replace(/^0/, "")}` };
                setOrgForm({ ...orgForm, phones });
              }}
            />
          ))}
          {(orgForm.socials || []).map((social, i) => (
            <div key={social.name} className="grid md:grid-cols-2 gap-4">
              <Field label={social.name} value={social.url || ""} onChange={(e) => {
                const socials = [...orgForm.socials];
                socials[i] = { ...social, url: e.target.value };
                setOrgForm({ ...orgForm, socials });
              }} />
            </div>
          ))}
          <Field
            label="Registered address line 1"
            value={orgForm.registeredAddress?.line1 || ""}
            onChange={(e) => setOrgForm({ ...orgForm, registeredAddress: { ...orgForm.registeredAddress, line1: e.target.value } })}
          />
          <Field
            label="Registered address line 2"
            value={orgForm.registeredAddress?.line2 || ""}
            onChange={(e) => setOrgForm({ ...orgForm, registeredAddress: { ...orgForm.registeredAddress, line2: e.target.value } })}
          />
          <Field
            label="Community venue"
            value={orgForm.venue?.name || ""}
            onChange={(e) => setOrgForm({ ...orgForm, venue: { ...orgForm.venue, name: e.target.value } })}
          />
          <button type="button" disabled={saving} onClick={() => save("org", orgForm)} className="px-6 py-3 rounded-full bg-[#EC4899] text-white">
            Save contact details
          </button>
        </Section>
        )}

        {canHero && (
        <Section title="Home hero">
          <Field label="Intro" rows={6} value={heroForm.body || ""} onChange={(e) => setHeroForm({ ...heroForm, body: e.target.value })} />
          <button type="button" disabled={saving} onClick={() => save("hero", heroForm)} className="px-6 py-3 rounded-full bg-[#EC4899] text-white">
            Save hero
          </button>
        </Section>
        )}

        {canStats && (
        <Section title="Home stats">
          {statsForm.map((stat, i) => (
            <div key={i} className="grid md:grid-cols-3 gap-4">
              <Field label="Value" value={stat.value} onChange={(e) => {
                const next = [...statsForm];
                next[i] = { ...stat, value: e.target.value };
                setStatsForm(next);
              }} />
              <Field label="Suffix" value={stat.suffix || ""} onChange={(e) => {
                const next = [...statsForm];
                next[i] = { ...stat, suffix: e.target.value };
                setStatsForm(next);
              }} />
              <Field label="Label" value={stat.label} onChange={(e) => {
                const next = [...statsForm];
                next[i] = { ...stat, label: e.target.value };
                setStatsForm(next);
              }} />
            </div>
          ))}
          <button type="button" disabled={saving} onClick={() => save("home_stats", statsForm)} className="px-6 py-3 rounded-full bg-[#EC4899] text-white">
            Save stats
          </button>
        </Section>
        )}

        {canFocus && (
        <Section title="What we do">
          <p className="text-sm text-black/60 -mt-2">
            These blocks appear on What we do. Add, remove, or reorder them. Leave the image URL blank to keep the bundled photo for a matching title.
          </p>
          {focusForm.map((item, i) => (
            <div key={item.id} className="space-y-3 pb-6 border-b border-black/5 last:border-0">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#EC4899]">Area {i + 1}</p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (i === 0) return;
                      const next = [...focusForm];
                      [next[i - 1], next[i]] = [next[i], next[i - 1]];
                      setFocusForm(next);
                    }}
                    className="w-9 h-9 grid place-items-center rounded-full border border-black/10 disabled:opacity-30"
                    aria-label="Move up"
                    disabled={i === 0}
                  >
                    <ArrowUp size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (i === focusForm.length - 1) return;
                      const next = [...focusForm];
                      [next[i + 1], next[i]] = [next[i], next[i + 1]];
                      setFocusForm(next);
                    }}
                    className="w-9 h-9 grid place-items-center rounded-full border border-black/10 disabled:opacity-30"
                    aria-label="Move down"
                    disabled={i === focusForm.length - 1}
                  >
                    <ArrowDown size={14} />
                  </button>
                  <button
                    type="button"
                    className="text-sm text-[#BE185D] hover:underline"
                    onClick={() => setFocusForm(focusForm.filter((_, idx) => idx !== i))}
                  >
                    Remove
                  </button>
                </div>
              </div>
              <Field label="Title" value={item.title} onChange={(e) => {
                const next = [...focusForm];
                next[i] = { ...item, title: e.target.value };
                setFocusForm(next);
              }} />
              <Field label="Text" rows={3} value={item.text} onChange={(e) => {
                const next = [...focusForm];
                next[i] = { ...item, text: e.target.value };
                setFocusForm(next);
              }} />
              <Field
                label="Bullet points (one per line)"
                rows={4}
                value={(item.bullets || []).join("\n")}
                onChange={(e) => {
                  const next = [...focusForm];
                  next[i] = {
                    ...item,
                    bullets: e.target.value.split("\n").map((line) => line.trim()).filter(Boolean),
                  };
                  setFocusForm(next);
                }}
              />
              <Field
                label="Image URL (optional)"
                value={persistableImage(item.image) ? item.image : ""}
                placeholder="https://… or upload a file"
                onChange={(e) => {
                  const next = [...focusForm];
                  next[i] = { ...item, image: e.target.value };
                  setFocusForm(next);
                }}
              />
              <div className="grid sm:grid-cols-[8rem_1fr] gap-4 items-start">
                <div className="aspect-[5/4] rounded-2xl overflow-hidden bg-black/5 grid place-items-center">
                  {item.image ? (
                    <img src={item.image} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-xs text-black/40 px-2 text-center">No image</span>
                  )}
                </div>
                <label className="block">
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-black/55">Upload image</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="mt-2 block w-full text-sm"
                    disabled={Boolean(uploadingKey)}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = "";
                      uploadFocusImage(i, file);
                    }}
                  />
                  <span className="text-xs text-black/45 mt-1 block">
                    {uploadingKey === item.id ? "Uploading…" : "JPEG, PNG or WebP, up to 8MB. Then save the section."}
                  </span>
                </label>
              </div>
            </div>
          ))}
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setFocusForm([...focusForm, emptyFocusArea()])}
              className="px-6 py-3 rounded-full border border-black/15 bg-white"
            >
              Add area
            </button>
            <button
              type="button"
              onClick={() => setFocusForm(toFocusForm(FOCUS_AREAS))}
              className="px-6 py-3 rounded-full border border-black/15 bg-white"
            >
              Load site copy
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => save(
                "focus_areas",
                focusForm
                  .filter((item) => item.title.trim())
                  .map((item, i) => ({
                    id: item.id || `focus-${i + 1}`,
                    title: item.title.trim(),
                    text: item.text || "",
                    bullets: item.bullets || [],
                    image: persistableImage(item.image),
                  }))
              )}
              className="px-6 py-3 rounded-full bg-[#EC4899] text-white"
            >
              Save focus areas
            </button>
          </div>
        </Section>
        )}

        {canServices && (
        <Section title="Services">
          <p className="text-sm text-black/60 -mt-2">
            These cards appear on Services & Programmes. Add bullet points on a new line for lists such as Other Services.
          </p>
          {servicesForm.map((item, i) => (
            <div key={i} className="space-y-3 pb-6 border-b border-black/5 last:border-0">
              <div className="flex items-center justify-between gap-3">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#EC4899]">Service {i + 1}</p>
                {servicesForm.length > 1 && (
                  <button
                    type="button"
                    className="text-sm text-[#BE185D] hover:underline"
                    onClick={() => setServicesForm(servicesForm.filter((_, idx) => idx !== i))}
                  >
                    Remove
                  </button>
                )}
              </div>
              <Field label="Title" value={item.title} onChange={(e) => {
                const next = [...servicesForm];
                next[i] = { ...item, title: e.target.value };
                setServicesForm(next);
              }} />
              <Field label="Text" rows={5} value={item.text} onChange={(e) => {
                const next = [...servicesForm];
                next[i] = { ...item, text: e.target.value };
                setServicesForm(next);
              }} />
              <Field
                label="Bullet points (one per line)"
                rows={6}
                value={(item.bullets || []).join("\n")}
                onChange={(e) => {
                  const next = [...servicesForm];
                  next[i] = {
                    ...item,
                    bullets: e.target.value.split("\n").map((line) => line.trim()).filter(Boolean),
                  };
                  setServicesForm(next);
                }}
              />
            </div>
          ))}
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setServicesForm([
                ...servicesForm,
                { title: "New service", text: "", bullets: [], image: "", accent: servicesForm.length % 2 ? "black" : "primary" },
              ])}
              className="px-6 py-3 rounded-full border border-black/15 bg-white"
            >
              Add service
            </button>
            <button
              type="button"
              onClick={() => setServicesForm(toServiceForm(SERVICES))}
              className="px-6 py-3 rounded-full border border-black/15 bg-white"
            >
              Load site copy
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => save("services", servicesForm.map((item, i) => ({
                title: item.title,
                text: item.text,
                bullets: item.bullets || [],
                image: item.image || services[i]?.image || SERVICES[i]?.image || "",
                accent: item.accent || services[i]?.accent || (i % 2 ? "black" : "primary"),
              })))}
              className="px-6 py-3 rounded-full bg-[#EC4899] text-white"
            >
              Save services
            </button>
          </div>
        </Section>
        )}

        {canProgrammes && (
        <Section title="Programmes">
          <Field label="Headline" value={programmesForm.headline || ""} onChange={(e) => setProgrammesForm({ ...programmesForm, headline: e.target.value })} />
          <Field label="Body" rows={4} value={programmesForm.body || ""} onChange={(e) => setProgrammesForm({ ...programmesForm, body: e.target.value })} />
          {(programmesForm.pillars || []).map((pillar, i) => (
            <div key={i} className="grid md:grid-cols-2 gap-4">
              <Field label={`Pillar ${i + 1}`} value={pillar.label} onChange={(e) => {
                const pillars = [...programmesForm.pillars];
                pillars[i] = { ...pillar, label: e.target.value };
                setProgrammesForm({ ...programmesForm, pillars });
              }} />
              <Field label="Text" value={pillar.text} onChange={(e) => {
                const pillars = [...programmesForm.pillars];
                pillars[i] = { ...pillar, text: e.target.value };
                setProgrammesForm({ ...programmesForm, pillars });
              }} />
            </div>
          ))}
          <button type="button" disabled={saving} onClick={() => save("programmes", programmesForm)} className="px-6 py-3 rounded-full bg-[#EC4899] text-white">
            Save programmes
          </button>
        </Section>
        )}

        {canEvents && (
        <Section title="Events">
          <p className="text-sm text-black/60 -mt-2">
            These cards appear on What we do. Set the date — after that day the event moves to Past events automatically.
          </p>
          {eventsForm.map((item, i) => {
            const upcoming = isUpcomingEvent(item);
            return (
              <div key={item.id || i} className="space-y-3 pb-6 border-b border-black/5 last:border-0">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#EC4899]">
                    Event {i + 1}
                    {item.date ? ` · ${upcoming ? "Upcoming" : "Past"} · ${formatEventDate(item.date)}` : ""}
                  </p>
                  <button
                    type="button"
                    className="text-sm text-[#BE185D] hover:underline"
                    onClick={() => setEventsForm(eventsForm.filter((_, idx) => idx !== i))}
                  >
                    Remove
                  </button>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <Field
                    label="Title"
                    value={item.title}
                    onChange={(e) => {
                      const next = [...eventsForm];
                      next[i] = { ...item, title: e.target.value };
                      setEventsForm(next);
                    }}
                  />
                  <Field
                    label="Date"
                    type="date"
                    value={item.date}
                    onChange={(e) => {
                      const next = [...eventsForm];
                      next[i] = { ...item, date: e.target.value };
                      setEventsForm(next);
                    }}
                  />
                </div>
                <Field
                  label="Location (optional)"
                  value={item.location || ""}
                  onChange={(e) => {
                    const next = [...eventsForm];
                    next[i] = { ...item, location: e.target.value };
                    setEventsForm(next);
                  }}
                />
                <Field
                  label="Details"
                  rows={3}
                  value={item.text || ""}
                  onChange={(e) => {
                    const next = [...eventsForm];
                    next[i] = { ...item, text: e.target.value };
                    setEventsForm(next);
                  }}
                />
              </div>
            );
          })}
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setEventsForm([
                ...eventsForm,
                normalizeEvent({ title: "", date: "", text: "", location: "" }, eventsForm.length),
              ])}
              className="px-6 py-3 rounded-full border border-black/15 bg-white"
            >
              Add event
            </button>
            <button
              type="button"
              onClick={() => setEventsForm(EVENTS.map((item, i) => normalizeEvent(item, i)))}
              className="px-6 py-3 rounded-full border border-black/15 bg-white"
            >
              Load site copy
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => save(
                "events",
                eventsForm
                  .map((item, i) => normalizeEvent(item, i))
                  .filter((item) => item.title && item.date)
              )}
              className="px-6 py-3 rounded-full bg-[#EC4899] text-white"
            >
              Save events
            </button>
          </div>
        </Section>
        )}

        {canGallery && (
        <Section title="Event gallery">
          <p className="text-sm text-black/60 -mt-2">
            Photos on What we do. Choose several files at once — they upload together, then save to publish.
          </p>
          <label className="block rounded-3xl border border-dashed border-black/15 bg-[#FDF2F8] px-6 py-8 text-center cursor-pointer hover:border-[#EC4899] transition-colors">
            <ImagePlus className="mx-auto text-[#EC4899]" size={28} />
            <span className="mt-3 block font-medium">
              {uploadingKey === "gallery" ? "Uploading…" : "Select multiple photos"}
            </span>
            <span className="mt-1 block text-sm text-black/50">JPEG, PNG, WebP or GIF, up to 8MB each</span>
            <input
              type="file"
              accept="image/*"
              multiple
              className="sr-only"
              disabled={Boolean(uploadingKey)}
              onChange={(e) => {
                uploadGalleryFiles(e.target.files);
                e.target.value = "";
              }}
            />
          </label>
          {galleryForm.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {galleryForm.map((item, i) => (
                <div key={item.id || i} className="rounded-2xl border border-black/8 overflow-hidden bg-white">
                  <div className="aspect-[4/5] bg-[#FDF2F8]">
                    {item.src ? (
                      <img src={item.src} alt={item.caption || `Gallery photo ${i + 1}`} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full grid place-items-center text-sm text-black/40">No image</div>
                    )}
                  </div>
                  <div className="p-4 space-y-3">
                    <Field
                      label="Caption"
                      value={item.caption}
                      onChange={(e) => {
                        const next = [...galleryForm];
                        next[i] = { ...item, caption: e.target.value };
                        setGalleryForm(next);
                      }}
                    />
                    <div className="flex flex-wrap items-center gap-3 text-sm">
                      <label className="text-[#EC4899] hover:underline cursor-pointer">
                        {uploadingKey === (item.id || String(i)) ? "Uploading…" : "Replace"}
                        <input
                          type="file"
                          accept="image/*"
                          className="sr-only"
                          disabled={Boolean(uploadingKey)}
                          onChange={(e) => {
                            replaceGalleryImage(i, e.target.files?.[0]);
                            e.target.value = "";
                          }}
                        />
                      </label>
                      <button
                        type="button"
                        className="inline-flex items-center disabled:opacity-30"
                        disabled={i === 0}
                        onClick={() => setGalleryForm(moveItem(galleryForm, i, -1))}
                        aria-label="Move up"
                      >
                        <ArrowUp size={16} />
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center disabled:opacity-30"
                        disabled={i === galleryForm.length - 1}
                        onClick={() => setGalleryForm(moveItem(galleryForm, i, 1))}
                        aria-label="Move down"
                      >
                        <ArrowDown size={16} />
                      </button>
                      <button
                        type="button"
                        className="ml-auto text-[#BE185D] hover:underline"
                        onClick={() => setGalleryForm(galleryForm.filter((_, idx) => idx !== i))}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setGalleryForm(toGalleryForm(GALLERY_IMAGES))}
              className="px-6 py-3 rounded-full border border-black/15 bg-white"
            >
              Load site photos
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => save(
                "gallery",
                galleryForm.map((item, i) => ({
                  id: item.id || `gal-${i + 1}`,
                  src: persistableImage(item.src),
                  caption: (item.caption || "").trim(),
                }))
              )}
              className="px-6 py-3 rounded-full bg-[#EC4899] text-white"
            >
              Save gallery
            </button>
          </div>
        </Section>
        )}
      </div>
    </div>
  );
}
